/**
 * useErrorHandler Hook
 * Provides error handling functions that use ErrorNotificationService
 * 
 * This hook initializes ErrorNotificationService with NotificationContext
 * and provides convenient error handling functions for components.
 */

import { useEffect, useCallback } from 'react';
import { useNotification } from '../contexts/NotificationContext';
import errorNotificationService from '../services/ErrorNotificationService';
import { wrapFirebaseOperation, wrapFirebaseBatch, isOffline, isNetworkError } from '../utils/firebaseErrorWrapper';

/**
 * Custom hook for error handling
 * @returns {Object} Error handling functions
 */
export const useErrorHandler = () => {
  const notificationContext = useNotification();

  // Initialize ErrorNotificationService when notification context is available
  useEffect(() => {
    if (notificationContext) {
      errorNotificationService.initialize({
        show: notificationContext.show,
        success: notificationContext.success,
        error: notificationContext.error,
        warning: notificationContext.warning,
        info: notificationContext.info,
        paymentSuccess: notificationContext.paymentSuccess
      });
    }
  }, [
    notificationContext?.show,
    notificationContext?.success,
    notificationContext?.error,
    notificationContext?.warning,
    notificationContext?.info,
    notificationContext?.paymentSuccess
  ]);

  /**
   * General error handler
   * @param {Error} error - The error object
   * @param {Object} options - Error handling options
   */
  const handleError = useCallback((error, options = {}) => {
    errorNotificationService.handleError(error, options);
  }, []);

  /**
   * Handle subscription errors
   * @param {Error} error - The error object
   * @param {Object} options - Error handling options
   */
  const handleSubscriptionError = useCallback((error, options = {}) => {
    errorNotificationService.handleSubscriptionError(error, {
      ...options,
      category: 'subscription'
    });
  }, []);

  /**
   * Handle validation errors
   * @param {Error} error - The error object
   * @param {Object} options - Error handling options
   */
  const handleValidationError = useCallback((error, options = {}) => {
    errorNotificationService.handleValidationError(error, {
      ...options,
      category: 'validation'
    });
  }, []);

  /**
   * Handle Firebase errors
   * @param {Error} error - The error object
   * @param {Object} options - Error handling options
   */
  const handleFirebaseError = useCallback((error, options = {}) => {
    errorNotificationService.handleFirebaseError(error, {
      ...options,
      category: 'firebase'
    });
  }, []);

  /**
   * Handle network errors
   * @param {Error} error - The error object
   * @param {Object} options - Error handling options
   */
  const handleNetworkError = useCallback((error, options = {}) => {
    errorNotificationService.handleNetworkError(error, {
      ...options,
      category: 'network'
    });
  }, []);

  /**
   * Wrap a Firebase operation to ensure errors are caught and displayed
   * @param {Function} operation - The Firebase operation function
   * @param {Object} options - Options for error handling
   * @returns {Promise} The result of the operation
   */
  const wrapOperation = useCallback((operation, options = {}) => {
    return wrapFirebaseOperation(operation, {
      ...options,
      context: options.context || 'Component'
    });
  }, []);

  /**
   * Wrap a Firebase batch operation
   * @param {Function} batchOperation - Function that returns a batch operation
   * @param {Object} options - Options for error handling
   * @returns {Promise} The result of the batch operation
   */
  const wrapBatch = useCallback((batchOperation, options = {}) => {
    return wrapFirebaseBatch(batchOperation, {
      ...options,
      context: options.context || 'Component'
    });
  }, []);

  return {
    handleError,
    handleSubscriptionError,
    handleValidationError,
    handleFirebaseError,
    handleNetworkError,
    // Firebase operation wrappers
    wrapOperation,
    wrapBatch,
    // Utility functions
    isOffline,
    isNetworkError,
    // Notification methods (for convenience)
    showSuccess: notificationContext?.success,
    showWarning: notificationContext?.warning,
    showInfo: notificationContext?.info,
    showError: notificationContext?.error,
    paymentSuccess: notificationContext?.paymentSuccess || notificationContext?.success
  };
};

export default useErrorHandler;


